import { useState } from "react";
import { useAuth, type UserProfile } from "./auth/auth-context";
import { SignIn } from "./auth/SignIn";
import { CatPlayground } from "./CatPlayground";
import { DreamStudio } from "./DreamStudio";
import { MediaLibrary } from "./MediaLibrary";
import { MemberChannel } from "./MemberChannel";
import { PeopleManager } from "./PeopleManager";

interface AuthorizedAppProps {
  profile: UserProfile;
  signOut: () => Promise<void>;
}

export function AuthorizedApp({ profile, signOut }: AuthorizedAppProps) {
  const [view, setView] = useState<"home" | "studio" | "channel" | "cat">("home");
  const [libraryRevision, setLibraryRevision] = useState(0);
  const isAdmin = profile.role === "admin";

  return (
    <div className="site-shell">
      <header className="masthead">
        <button className="wordmark" onClick={() => setView("home")} aria-label="Cat Network home">
          <span className="wordmark-mark">C</span>
          <span>CAT NETWORK</span>
        </button>
        <nav className="masthead-actions" aria-label="Account">
          {isAdmin && (
            <button
              className={view === "studio" ? "nav-button active" : "nav-button"}
              onClick={() => setView(view === "studio" ? "home" : "studio")}
            >
              Studio
            </button>
          )}
          {view !== "home" && (
            <button className="nav-button" onClick={() => setView("home")}>Channels</button>
          )}
          <button className="nav-button muted" onClick={() => void signOut()}>Sign out</button>
        </nav>
      </header>

      {view === "studio" && isAdmin ? (
        <main className="studio" data-testid="admin-studio">
          <p className="eyebrow">Broadcast control</p>
          <h1>Studio</h1>
          <p className="lede">Build the channel without exposing unfinished work to the audience.</p>
          <div className="studio-grid studio-overview">
            <article><span>01</span><h2>Library</h2><p>Import and organize private photos and videos.</p></article>
            <article><span>02</span><h2>Dreams</h2><p>Turn selected memories into video concepts before generating anything.</p></article>
            <article><span>03</span><h2>People</h2><p>Add family members to the private guest list.</p></article>
          </div>
          <MediaLibrary onLibraryChanged={() => setLibraryRevision((current) => current + 1)} />
          <DreamStudio libraryRevision={libraryRevision} />
          <PeopleManager />
        </main>
      ) : view === "channel" ? (
        <MemberChannel />
      ) : view === "cat" ? (
        <CatPlayground />
      ) : (
        <main className="home">
          <section className="hero">
            <p className="eyebrow">Private broadcast · Members only</p>
            <h1>Who’s watching?</h1>
            <p className="lede">Choose your side of the screen.</p>
          </section>

          <section className="audience-grid" aria-label="Choose an experience">
            <button className="audience-card cat-card" onClick={() => setView("cat")}>
              <span className="channel-number">01</span>
              <span className="card-art" aria-hidden="true">●</span>
              <span className="card-copy"><strong>I’m a cat</strong><small>Chase, pounce, repeat</small></span>
              <span className="arrow" aria-hidden="true">↗</span>
            </button>
            <button className="audience-card human-card" onClick={() => setView("channel")}>
              <span className="channel-number">02</span>
              <span className="card-art dream-art" aria-hidden="true">✦</span>
              <span className="card-copy"><strong>I’m here to see cats</strong><small>Memories, movies, and dreams</small></span>
              <span className="arrow" aria-hidden="true">↗</span>
            </button>
          </section>

          <footer className="member-strip">
            <span className="live-dot" />
            <span>Signed in as {profile.email}</span>
            <span className="member-role">{isAdmin ? "Administrator" : "Family member"}</span>
          </footer>
        </main>
      )}
    </div>
  );
}

export default function App() {
  const { state, signOut } = useAuth();

  if (state.status === "allowed") {
    return <AuthorizedApp profile={state.profile} signOut={signOut} />;
  }

  return (
    <main className="auth-screen">
      <div className="auth-card">
        <div className="auth-monogram">C</div>
        <p className="eyebrow">cats.mattfinkel.com</p>
        <h1>Cat Network</h1>
        <p>A private channel for a very small audience.</p>
        {state.status === "idle" && <SignIn />}
        {state.status === "verifying" && <div className="auth-status">Checking the guest list…</div>}
        {state.status === "denied" && (
          <div className="denied-block">
            <div className="auth-status denied">Access denied: {state.reason}</div>
            <SignIn />
          </div>
        )}
      </div>
    </main>
  );
}
